/**
 * CustomFontColorUI Plugin for CKEditor
 * 
 * This plugin adds a UI component to CKEditor for selecting and applying custom font colors. It provides a dropdown
 * menu containing predefined color options and a remove button to clear the applied color. The dropdown and buttons
 * integrate with the editor's commands and focus tracking.
 * 
 * ## Features
 * - **Dropdown Interface**:
 *   - Displays a grid of predefined color options for easy selection.
 *   - Includes an arrow indicator for dropdown status.
 *   - Tooltip-enabled for better accessibility.
 * - **Color Buttons**:
 *   - Each button is styled with the corresponding color.
 *   - Clicking a button applies the color to the selected text.
 *   - Integrated with the `CustomFontColor` command.
 * - **Remove Color Button**:
 *   - Clears the applied font color and restores the default styling.
 *   - Accessible and visually distinct in the UI.
 * - **Focus Handling**:
 *   - Automatically closes the dropdown when the editor or dropdown loses focus.
 *   - Synchronizes dropdown state with the editor's focus tracker.
 * - **Customization**:
 *   - Supports dynamic configuration of color options via editor configuration.
 * 
 * ## Implementation Details
 * - **Initialization**:
 *   - Configures the dropdown and buttons in the editor's UI component factory.
 *   - Reads color options from the editor's configuration.
 * - **Event Handling**:
 *   - Listens for focus changes and toggles dropdown visibility accordingly.
 *   - Executes editor commands to apply or remove font colors on button actions.
 * - **Template Structure**:
 *   - Color buttons are displayed in a grid layout with customizable columns.
 *   - Dropdown includes a button, arrow indicator, color grid, and remove button.
 * 
 * ## Usage
 * This plugin is automatically loaded when added to the CKEditor configuration:
 * ```javascript
 * ClassicEditor
 *     .create(document.querySelector('#editor'), {
 *         plugins: [CustomFontColorUI, ...],
 *         toolbar: ['CustomFontColor', ...],
 *         CustomFontColor: {
 *             colors: [
 *                 { name: 'Red', class: 'red' },
 *                 { name: 'Blue', class: 'blue' },
 *                 ...
 *             ]
 *         }
 *     })
 *     .then(editor => {
 *         console.log('Editor loaded successfully');
 *     })
 *     .catch(error => {
 *         console.error('Editor initialization error:', error);
 *     });
 * ```
 * 
 * ## References
 * - CKEditor UI API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_ui_view-View.html
 * - CKEditor Plugin API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_core_plugin-Plugin.html
 * 
 * @module CustomFontColorUI
 * @exports CustomFontColorUI
 */

import { CKPlugins } from '../../constants/constants';

const {
    DropdownView,
    DropdownPanelView,
    ButtonView,
    Plugin,
    IconView,
    View
} = window.ClassicEditor;

const fontColorIcon = '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M12.4 10.3 10 4.5l-2.4 5.8h4.8zm.5 1.2H7.1L5.7 15H4.2l5-12h1.6l5 12h-1.5L13 11.5zm3.1 7H4a1 1 0 0 1 0-2h12a1 1 0 0 1 0 2z"/></svg>';
const arrowDownIcon = '<svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M.941 4.523a.75.75 0 1 1 1.06-1.06l3.006 3.005 3.005-3.005a.75.75 0 1 1 1.06 1.06l-3.549 3.55a.75.75 0 0 1-1.168-.136L.941 4.523z"></path></svg>';
const removeIcon = '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M8.636 9.531l-2.758 3.94a.5.5 0 0 0 .122.696l3.224 2.284h1.314l2.636-3.736L8.636 9.53zm.288 8.451L5.14 15.396a2 2 0 0 1-.491-2.786l6.673-9.53a2 2 0 0 1 2.785-.49l3.742 2.62a2 2 0 0 1 .491 2.785l-7.269 10.053-2.147-.066z"></path><path d="M4 18h5.523v-1H4zm-2 0h1v-1H2z"></path></svg>';
const buttonIcon = '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M16.935 5.328a2 2 0 0 1 0 2.829l-7.778 7.778a2 2 0 0 1-2.829 0L3.5 13.107a1.999 1.999 0 1 1 2.828-2.829l.707.707a1 1 0 0 0 1.414 0l5.658-5.657a2 2 0 0 1 2.828 0z" class="ck-icon__fill" style="fill: rgb(255, 255, 255);"></path><path d="M14.814 6.035L8.448 12.4a1 1 0 0 1-1.414 0l-1.413-1.415A1 1 0 1 0 4.207 12.4l2.829 2.829a1 1 0 0 0 1.414 0l7.778-7.778a1 1 0 1 0-1.414-1.415z"></path></svg>';

export class CustomFontColorUI extends Plugin {
    icon: any;
    commandName: any;
    componentName: any;
    dropdownLabel: any;
    removeButtonLabel: any;
    editor: any;
    dropdownView: any;
    colorButtonList: any[] = [];
    arrowView: any;
    removeColorButton: any;

	constructor(editor) {
        super(editor);

        const t = editor.locale.t;
        this.icon = fontColorIcon;
        this.commandName = CKPlugins.CustomFontColor;
        this.componentName = CKPlugins.CustomFontColor;
        this.dropdownLabel = t('Font Color');
        this.removeButtonLabel = t('Remove Color');
	}

	static get pluginName() {
		return 'CustomFontColorUI';
	}

    init() {
        const editor = this.editor;
        const colors = editor.config.get(this.componentName).colors;
        const locale = editor.locale;

        this.arrowView = this.createArrowView();
        this.removeColorButton = this.createRemoveColorButton();
        this.dropdownView = new DropdownView(locale, new ButtonView(), new DropdownPanelView());
        this.dropdownView.buttonView.set({
            icon: fontColorIcon,
            label: this.dropdownLabel,
            tooltip: true,
        });

        this.dropdownView.buttonView.on('execute', () => this.toggleDrodown());

        this.dropdownView.class = 'ck-dropdown_color';

        this.dropdownView.panelView.children.add(this.removeColorButton);
        if (this.dropdownView.buttonView.isRendered) {
            this.dropdownView.buttonView.children.add(this.arrowView);
        } else {
            this.dropdownView.buttonView.once('render', () => {
                this.dropdownView.buttonView.children.add(this.arrowView);
            });
        }

        const viewStyleAttribute = {
			gridTemplateColumns: `repeat( ${ 5 }, 1fr)`
		};

        this.colorButtonList = this.createColorButtonList(editor, locale, colors);

        const view = new View();
        view.setTemplate({
            tag: 'div',
			children: this.colorButtonList,
			attributes: {
				class: [
					'ck',
					'ck-color-grid'
				],
				style: viewStyleAttribute
			}
        });

        this.dropdownView.panelView.children.add(view);
        editor.ui.componentFactory.add(CKPlugins.CustomFontColor, () => this.dropdownView);

        editor.ui.focusTracker.on('change:isFocused', (evt, name, isFocused) => {
            if (!isFocused) {
                this.dropdownView.isOpen = false;
            }
        });

        this.dropdownView.focusTracker.on('change:isFocused', (evt, name, isFocused) => {
            if (!isFocused) {
                this.dropdownView.isOpen = false;
            }
        });

        this.dropdownView.on('change:isOpen', (evt, name, isOpen) => {
            const element = this.dropdownView.buttonView.element;
            isOpen ? this.selectColorButton(element) : this.unselectColorButton(element);
        });
    }

    toggleDrodown() {
        this.dropdownView.isOpen = !this.dropdownView.isOpen;
    }

    private createRemoveColorButton() {
        const buttonView = new ButtonView();

        buttonView.set( {
            withText: true,
            icon: removeIcon,
            label: this.removeButtonLabel
        } );

        buttonView.class = 'ck-color-selector__remove-color';
        buttonView.on('execute', () => {
            this.unselectAllColorButtons();
            this.toggleDrodown();
            this.editor.execute(this.commandName, { value: null });
        } );

        buttonView.render();

        return buttonView;
    }

    private createColorButtonList(editor: any, locale: any, colors: any[]) {
        return colors.map(color => {
            const buttonView = new ButtonView(locale);
            buttonView.set({
                label: color.name,
                withText: true,
                icon: buttonIcon,
                class: color.class + ' ck-color-grid__tile ck-color-table__color-tile_bordered'
            });

            buttonView.on('execute', () => {
                this.unselectAllColorButtons();
                this.selectColorButton(buttonView.element);
                this.toggleDrodown();
                editor.execute(this.commandName, { value: color.class });
            });

            return buttonView;
        });
    }

    private createArrowView() {
		const arrowView = new IconView();

		arrowView.content = arrowDownIcon;

		arrowView.extendTemplate( {
			attributes: {
				class: 'ck-dropdown__arrow'
			}
		} );

		return arrowView;
	};

    private unselectAllColorButtons = () => {
        this.colorButtonList.map(item => {
            this.unselectColorButton(item.element);
        });
    };

    private selectColorButton = (element: Element) => {
        element.classList.add('ck-on');
        element.classList.remove('ck-off');
    };

    private unselectColorButton = (element: Element) => {
        element.classList.add('ck-off');
        element.classList.remove('ck-on');
    };
}
